import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
import { installOfficeDialogShims } from "./utils/officeDialogShims";
import "./styles/theme.css";
import "./styles/tailwind.css";

const LoadingFallback = () => (
  <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-background p-6" style={{ minWidth: 320, maxWidth: 400 }}>
    <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    <p className="text-sm text-muted-foreground">Loading IntelliDocSuite...</p>
  </div>
);

const ErrorFallback = ({ onRetry }: { onRetry: () => void }) => (
  <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-background p-6 text-center" style={{ minWidth: 320, maxWidth: 400 }}>
    <p className="text-sm text-destructive">Unable to load the add-in.</p>
    <div className="flex gap-2">
      <button type="button" onClick={onRetry} className="rounded bg-primary px-4 py-2 text-sm text-white hover:bg-primary/90">
        Retry
      </button>
      <button
        type="button"
        onClick={() => {
          if (typeof Office !== "undefined" && (Office as { addin?: { hide: () => void } }).addin) {
            (Office as { addin: { hide: () => void } }).addin.hide();
          } else {
            window.close?.();
          }
        }}
        className="rounded border px-4 py-2 text-sm hover:bg-gray-100"
      >
        Close
      </button>
    </div>
  </div>
);

type BootState = "loading" | "ready" | "error";

const Bootstrapper = () => {
  const [state, setState] = useState<BootState>("loading");

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const setTaskpaneWidthIfSupported = () => {
      if (typeof Office === "undefined") return;
      if (Office.context?.requirements?.isSetSupported?.("TaskPaneApi", "1.1")) {
        Office.extensionLifeCycle?.taskpane?.setWidth?.(500);
      }
    };

    const goReady = () => {
      if (timeoutId) clearTimeout(timeoutId);
      setTaskpaneWidthIfSupported();
      installOfficeDialogShims();
      setState("ready");
    };

    if (typeof Office !== "undefined" && Office.onReady) {
      Office.onReady()
        .then(goReady)
        .catch(() => setState("error"));
      // Fallback: if Office.onReady never resolves (e.g. some Word/WebView cases), show app after 8s
      timeoutId = setTimeout(goReady, 8000);
    } else {
      goReady();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  if (state === "loading") return <LoadingFallback />;
  if (state === "error") return <ErrorFallback onRetry={() => window.location.reload()} />;
  return (
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
};

const root = document.getElementById("root");
if (root) {
  ReactDOM.createRoot(root).render(<Bootstrapper />);
}

